import type { DeclaredSurface } from "./declared-surface";
/**
 * Centralized category slug constants.
 *
 * Single source of truth for the 9 category slugs used across client and
 * server code. When renaming a category, update the slug value here and
 * TypeScript will surface every consumer that needs updating.
 *
 * Section slugs (e.g. `password-log-devices-local-access`) are NOT included
 * here — they live in a separate constants file (Phase 2 of the constants
 * migration). Field keys are Phase 3.
 *
 * Established session 39 after Required Task #2 exposed hardcoded category
 * slugs scattered across 6+ files, causing a follow-up commit and Playwright
 * verification to clean up after a slug rename.
 */

export const CATEGORY_SLUGS = {
  ABOUT_YOU:               'about-you',
  PROTECT_YOUR_ACCESS:     'protect-your-access', // s111 Build 6 — new category between About You and Legal
  MY_WISHES_AND_LEGACY:    'instructions-for-executors',
  LEGAL:                   'legal',
  FINANCIAL_AND_INSURANCE: 'financial-and-insurance',
  ASSETS:                  'assets',
  PERSONAL_INFORMATION:    'personal-information',
  CHILDREN_AND_DEPENDENTS: 'children-and-dependents',
  FAMILY_FRIENDS_AND_PETS: 'family-friends-and-pets',
  ONLINE_PRESENCE:         'online-presence',
  WHERE_IS_EVERYTHING:     'where-is-everything',
} as const;

export type CategorySlug = typeof CATEGORY_SLUGS[keyof typeof CATEGORY_SLUGS];

/**
 * Categories that exist ONLY for the planner — surfaces a read-only viewer (executor, and any
 * future read-only role) must never be offered.
 *
 * ## Why this is a declaration and not an `if`
 *
 * DS-1 option (iii) — co-located declaration + aggregator visibility (`ds1-phase0-s170.md`).
 * The reason each category is planner-only lives HERE, next to the slug it describes, rather than
 * as an unexplained `slug !== 'protect-your-access'` in whichever renderer happened to need it.
 * Consumers read the aggregator (`isPlannerOnlyCategory`) and never re-test the slug themselves,
 * so adding a second planner-only category is a one-line edit in this file instead of a hunt
 * through every nav and route.
 *
 * 🔴 **Registered DS-1 census member at creation** (D-169.7 precedent — recorded when written, not
 * left for a later census to discover).
 *
 * ## What membership means
 *
 * Two independent consequences, both required — a category listed here must be BOTH:
 *   1. filtered out of read-only navigation (`ExecutorSidebar`), and
 *   2. gated at its mount with a fallback (`GenericCategoryPage`), so a direct URL degrades to an
 *      explanation rather than a crash.
 * Membership is not a security boundary on its own; it is the declaration those two guards read.
 */
export const PLANNER_ONLY_CATEGORIES: Readonly<Record<string, string>> = {
  // s171 (F-S170.1): Protect Your Access is the secure-file setup home (essential #1). Its whole
  // surface is the planner's own .oesif setup — second-person setup copy, a create-file CTA, and
  // Cat A affordances that route through `SensitiveDataSessionProvider`, which the executor shell
  // deliberately does not mount (Cat A values never transit the server; the executor's own SIN
  // path is client-side and lives elsewhere). There is no executor reading of this category: the
  // secure file is possession-based, so what an executor needs is the FILE, not this page.
  [CATEGORY_SLUGS.PROTECT_YOUR_ACCESS]:
    'Secure-file setup surface — planner-only by design; Cat A affordances need a provider the read-only shells do not mount.',
} as const;

/**
 * The per-surface predicate. Every consumer asks THIS — never a bare slug comparison.
 */
export function isPlannerOnlyCategory(slug: string): boolean {
  return Object.prototype.hasOwnProperty.call(PLANNER_ONLY_CATEGORIES, slug);
}

/**
 * DS-1 member #9 (created s171 Block 1, registered into the census here in Block B).
 *
 * 🔴 Its KIND differs from the other members, and that difference is deliberate rather than a
 * shortfall. The eight charter surfaces declare FIELD-KEY reads that flow through the four
 * field-read chokepoints; this one declares CATEGORY SLUGS read by a nav filter and a render gate.
 * So it contributes no `section::field` refs to the coverage guard, and the Block D
 * anti-recurrence scan — which keys on those chokepoints — will not cover it. That is by design:
 * the scan bans an undeclared field-read shape, and this is not one. What the census gives it is
 * visibility: a tenth planner-only category joins a list someone reads, instead of becoming a
 * second `slug !== '…'` somewhere.
 */
export const PLANNER_ONLY_CATEGORY_SURFACE: DeclaredSurface = {
  id: "M9:planner-only-categories",
  where: "shared/constants/categories.ts → PLANNER_ONLY_CATEGORIES / isPlannerOnlyCategory",
  why: "Categories a read-only viewer must never be offered. Membership carries two consequences that must BOTH hold: filtered out of read-only navigation, and gated at the mount with a fallback so a direct URL degrades to an explanation rather than the app-tree crash F-S170.1 produced.",
  read: { kind: "category-slugs", slugs: Object.keys(PLANNER_ONLY_CATEGORIES) },
};
